import { Injectable, inject, signal } from '@angular/core';
import { GlobalsService } from './globals.service';
import { ApiService } from './api.service';
import { PathStructureInfo, ServerFile, ServerInfo } from '../models';
import { firstValueFrom, catchError, of } from 'rxjs';

/**
 * Path Service - converted from PathUtil.as
 * 
 * Manages server URLs and path structures for file access.
 * In the legacy system, files are stored on multiple servers with path structures
 * that define how to transform serverName into a valid URL path.
 * 
 * AMFphp Service: CommonService
 * - getServers() - returns array of ServerInfo
 * - getPathStructures() - returns array of PathStructureInfo
 */
@Injectable({
  providedIn: 'root'
})
export class PathService {
  private readonly globals = inject(GlobalsService);
  private readonly api = inject(ApiService);

  // Server and path data - populated on init
  private _servers = signal<ServerInfo[]>([]);
  private _paths = signal<PathStructureInfo[]>([]);
  private _initialized = signal(false);

  // Object-specific server assignments (from object_configs table)
  private _projectsObjectServer = signal<ServerInfo | null>(null);

  // Public readonly signals
  readonly servers = this._servers.asReadonly();
  readonly paths = this._paths.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly projectsObjectServer = this._projectsObjectServer.asReadonly();
  
  // Default servers (from observed data)
  private readonly defaultStorageServer = 'storage-b2.dreambooksworld.com';
  private readonly projectsServer = 'express-cloud-storage-1.s3.eu-west-1.amazonaws.com';

  // Hetzner Object Storage (single source of truth for Angular-side Hetzner URLs)
  private readonly hetznerBaseUrl = 'https://nbg1.your-objectstorage.com/express-cloud-storage-1';
  
  // CMS images server (for product/subcategory images)
  // Note: Using HTTP temporarily if HTTPS has certificate issues
  private readonly cmsImagesServer = 'express-cloud-storage-1.s3.eu-west-1.amazonaws.com';
  private readonly cmsImagesPath = 'cmsproimages';
  private readonly cmsImagesProtocol = 'https'; // Change to 'http' if SSL certificate is invalid
  
  // Avatars path
  private readonly avatarsPath = 'express/avatars';

  /**
   * Initialize with pre-fetched data (used by gallery viewer public endpoint).
   */
  initWithData(servers: ServerInfo[], paths: PathStructureInfo[]): void {
    if (this._initialized()) return;
    this._servers.set(servers);
    this._paths.set(paths);
    this._initialized.set(true);
  }

  /**
   * Initialize servers and paths from backend
   * Call this on app startup after login
   */
  async init(): Promise<void> {
    if (this._initialized()) {
      console.log('[PathService] Already initialized');
      return;
    }

    console.log('[PathService] Initializing...');
    
    try {
      // Get servers from backend
      // API returns { servers: [...] } wrapper
      const response = await firstValueFrom(
        this.api.get<any>('/common/servers').pipe(
          catchError(err => {
            console.warn('[PathService] Error loading servers:', err);
            return of(null);
          })
        )
      );

      const servers: ServerInfo[] = response?.servers || (Array.isArray(response) ? response : []);
      if (servers.length) {
        this._servers.set(servers);
        console.log('[PathService] Servers loaded:', servers);
      } else {
        console.warn('[PathService] No servers returned, using defaults');
      }
    } catch (e) {
      console.warn('[PathService] Could not load servers:', e);
    }

    try {
      // Get path structures from backend
      // API returns { pathStructures: [...] } wrapper
      const response = await firstValueFrom(
        this.api.get<any>('/common/path-structures').pipe(
          catchError(err => {
            console.warn('[PathService] Error loading paths:', err);
            return of(null);
          })
        )
      );

      const paths: PathStructureInfo[] = response?.pathStructures || (Array.isArray(response) ? response : []);
      if (paths.length) {
        this._paths.set(paths);
        console.log('[PathService] Paths loaded:', paths);
      } else {
        console.warn('[PathService] No paths returned, using defaults');
      }
    } catch (e) {
      console.warn('[PathService] Could not load paths:', e);
    }

    // Resolve the active storage server from the loaded servers list (enable=1)
    const loadedServers = this._servers();
    const enabledServer = loadedServers.find(s => Number(s.enable) === 1);
    if (enabledServer) {
      this._projectsObjectServer.set(enabledServer);
      console.log('[PathService] Active storage server (enable=1):', enabledServer);
    } else {
      console.warn('[PathService] No enabled server found in servers list');
    }

    this._initialized.set(true);
    console.log('[PathService] Initialization complete');
  }

  /**
   * Get the server ID that should be used for new projects.
   * Returns the enabled server (enable=1) from the loaded servers list.
   */
  getProjectsServerId(): number | undefined {
    return this._projectsObjectServer()?.id ?? undefined;
  }

  /**
   * Get server URL by ID. Returns empty string when the server can't be
   * resolved — callers MUST check for empty and propagate up the chain
   * rather than concatenating, otherwise the result becomes a relative URL
   * resolved against the SPA origin and 404s.
   *
   * Previously this method silently fell back to a hardcoded host
   * (`storage-b2.dreambooksworld.com`) which masked configuration / data
   * issues. The fallback was removed deliberately: callers like
   * `getProjectThumbnailUrl` already handle empty by returning empty, and
   * the album form then renders a placeholder rather than a broken image.
   *
   * The `fallbackHost` parameter is retained for source compatibility but
   * no longer used.
   */
  getServerUrl(serverId: number, _fallbackHost?: string): string {
    const serversList = this._servers();

    if (!serversList.length) {
      console.warn('[PathService] getServerUrl called before servers list was loaded — returning empty URL');
      return '';
    }

    // Convert to number in case it's a string from AMF
    const sId = Number(serverId);
    let server: ServerInfo | undefined;

    if (sId === 0) {
      // CMS server
      server = serversList.find(s => Number(s.cms) === 1);
    } else {
      server = serversList.find(s => Number(s.id) === sId);
    }

    if (!server) {
      console.warn(
        `[PathService] Server ${serverId} not resolvable — returning empty URL. ` +
        `If serverId=0, the loaded servers list has no record with cms=1. ` +
        `If serverId>0, the value doesn't match any loaded server. ` +
        `Most common upstream cause: a draft was saved without a real serverId on its project records.`
      );
      return '';
    }

    // Use serverUrl (DB server_url column) or server (DB server column) or url (legacy AMF)
    const serverUrlStr = server.serverUrl || server.server || server.url;
    if (!serverUrlStr) {
      console.warn(`[PathService] Server ${serverId} resolved but has no URL — returning empty`);
      return '';
    }

    // If serverUrl already includes protocol, use as-is
    if (serverUrlStr.startsWith('http')) {
      return serverUrlStr.endsWith('/') ? serverUrlStr : serverUrlStr + '/';
    }

    // For new API (no ssl field), default to globals ssl; for legacy, respect per-server ssl flag
    const useHttps = server.ssl != null
      ? (this.globals.config().ssl && Number(server.ssl) === 1)
      : this.globals.config().ssl;
    const protocol = useHttps ? 'https' : 'http';
    return `${protocol}://${serverUrlStr}/`;
  }

  /**
   * Get path structure by ID
   */
  getPath(pathId: number): PathStructureInfo | undefined {
    // Convert to number in case it's a string from AMF
    const pId = Number(pathId);
    return this._paths().find(p => Number(p.id) === pId);
  }

  /**
   * True when the value is an absolute http(s) URL we can use as-is. Used to
   * prefer backend-provided file URLs over reconstructing them from
   * serverId/serverName/pathStructure (which depends on the servers list and
   * a real serverId being persisted).
   */
  private isUsableUrl(url: string | null | undefined): url is string {
    return typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));
  }

  /**
   * Get full URL for a ServerFile
   * Replicates PathUtil.getUrl() from legacy
   */
  getUrl(file: ServerFile | undefined): string {
    if (!file) return '';
    // Prefer the backend-provided absolute URL when present — avoids the
    // servers-list / path-structure reconstruction and its timing pitfalls.
    if (this.isUsableUrl(file.url)) return file.url;
    if (!file.serverName) return '';

    // Path structure 21: Hetzner Object Storage for low-res gallery photos
    if (file.pathStructureId === 21) {
      const sn = file.serverName;
      const dir1 = sn.substring(0, 2);
      const dir2 = sn.substring(2, 4);
      const dir3 = sn.substring(4, 6);
      return `${this.hetznerBaseUrl}/lr-gallery-photos/${dir1}/${dir2}/${dir3}/${sn}`;
    }

    const pathStructure = this.getPath(file.pathStructureId);
    
    if (pathStructure && pathStructure.match && pathStructure.replace) {
      // Use path structure to transform serverName
      const transformedPath = file.serverName.replace(
        new RegExp(pathStructure.match),
        pathStructure.replace
      );
      const base = this.getServerUrl(file.serverId);
      // Empty base means the server isn't resolvable — return empty so the
      // caller doesn't end up with a relative URL that 404s against the
      // SPA origin. The downstream consumer (e.g. img src binding) treats
      // empty as "no image" and renders a placeholder.
      return base ? base + transformedPath : '';
    }

    // Fallback: construct URL from serverName using observed pattern
    // Pattern: first 6 chars split into 3 pairs (xx/xx/xx/) as directory structure
    return this.constructDefaultUrl(file);
  }

  /**
   * Construct URL using default pattern observed in storage server
   * Pattern: express/photos/serverName[0:2]/serverName[2:4]/serverName[4:6]/serverName
   * Example: 610c14a7b2ad473b148aff3a4ada54b0.png -> express/photos/61/0c/14/610c14a7b2ad473b148aff3a4ada54b0.png
   */
  private constructDefaultUrl(file: ServerFile): string {
    const serverName = file.serverName;
    const base = this.getServerUrl(file.serverId);
    if (!base) return '';
    if (!serverName || serverName.length < 6) {
      return base + 'express/photos/' + serverName;
    }

    // Create path structure: express/photos/xx/xx/xx/filename
    const dir1 = serverName.substring(0, 2);
    const dir2 = serverName.substring(2, 4);
    const dir3 = serverName.substring(4, 6);
    const path = `express/photos/${dir1}/${dir2}/${dir3}/${serverName}`;

    return base + path;
  }

  /**
   * Get project URL (for .dbproject files)
   * Projects are stored on AWS S3 with specific structure.
   * Pattern: express/projects/xx/xx/xx/{serverName}.dbproject
   */
  getProjectUrl(project: ServerFile): string {
    if (!project) return '';
    // Prefer the backend-provided .dbproject URL when present.
    if (this.isUsableUrl(project.url)) return project.url;
    if (!project.serverName) return '';

    const serverName = project.serverName;
    const baseUrl = this.getServerUrl(project.serverId);
    // Empty base means the server isn't resolvable — return empty so the
    // caller (e.g. GuestDraftLoaderService.loadSceneViaPathService) can
    // surface "no project URL" cleanly instead of fetching a relative URL
    // against the SPA origin.
    if (!baseUrl) return '';

    // Try path structure transformation if available
    if (project.pathStructureId) {
      const pathStructure = this.getPath(project.pathStructureId);
      if (pathStructure?.match && pathStructure?.replace) {
        let transformedPath = serverName.replace(
          new RegExp(pathStructure.match),
          pathStructure.replace
        );
        // Ensure .dbproject extension is present
        if (!transformedPath.endsWith('.dbproject')) {
          transformedPath += '.dbproject';
        }
        return baseUrl + transformedPath;
      }
    }

    // Default pattern: express/projects/xx/xx/xx/{serverName}.dbproject
    if (serverName.length < 6) {
      return `${baseUrl}express/projects/${serverName}.dbproject`;
    }

    const dir1 = serverName.substring(0, 2);
    const dir2 = serverName.substring(2, 4);
    const dir3 = serverName.substring(4, 6);

    return `${baseUrl}express/projects/${dir1}/${dir2}/${dir3}/${serverName}.dbproject`;
  }

  /**
   * Get project thumbnail URL
   * Project thumbnails are stored in a thumbnails/ subdirectory at the directory level (not in serverName folder).
   * Pattern: express/projects/xx/xx/xx/thumbnails/{serverName}.png
   *
   * @param serverName - Project serverName (UUID format)
   * @param serverId - Optional server ID (not currently used, reserved for future)
   * @param pathStructureId - Optional path structure ID (not currently used, reserved for future)
   * @returns Full URL to the project thumbnail
   */
  getProjectThumbnailUrl(serverName: string | null, serverId?: number | null, pathStructureId?: number | null, thumbnailUrl?: string | null): string {
    // Prefer the backend-provided thumbnail URL when present (callers that
    // have the project/draft metadata can pass draft.thumbnailUrl).
    if (this.isUsableUrl(thumbnailUrl)) return thumbnailUrl;
    if (!serverName) return '';

    // Resolve server dynamically from the project's serverId. Drafts that
    // were saved without a serverId (or with serverId=0) used to fall
    // back to a CMS lookup which silently routed to a hardcoded host —
    // now we return empty so the album form renders a placeholder
    // thumbnail and surfaces the upstream data issue (project save
    // didn't persist serverId) instead of papering over it.
    const baseUrl = (serverId != null && serverId > 0)
      ? this.getServerUrl(serverId)
      : this.getServerUrl(0);
    if (!baseUrl) return '';

    // Project thumbnails: express/projects/xx/xx/xx/thumbnails/{serverName}.png
    if (serverName.length < 6) {
      return `${baseUrl}express/projects/thumbnails/${serverName}.png`;
    }

    const dir1 = serverName.substring(0, 2);
    const dir2 = serverName.substring(2, 4);
    const dir3 = serverName.substring(4, 6);

    return `${baseUrl}express/projects/${dir1}/${dir2}/${dir3}/thumbnails/${serverName}.png`;
  }

  /**
   * Get thumbnail URL for a file
   * Thumbnails have _t suffix before extension
   */
  getThumbnailUrl(file: ServerFile | undefined): string {
    if (!file) return '';
    // Prefer a backend-provided thumbnail URL; fall back to deriving the
    // _t-suffixed name from serverName.
    if (this.isUsableUrl(file.thumbnailUrl)) return file.thumbnailUrl;
    if (this.isUsableUrl(file.thumbUrl)) return file.thumbUrl;
    if (!file.serverName) return '';

    // Create thumbnail version of serverName (replace any existing size suffix with _t)
    const serverName = file.serverName;
    const lastDot = serverName.lastIndexOf('.');

    let thumbServerName: string;
    if (lastDot > 0) {
      const baseName = serverName.substring(0, lastDot).replace(/_(ts|t|s|m|b|l|e)$/, '');
      thumbServerName = baseName + '_t' + serverName.substring(lastDot);
    } else {
      thumbServerName = serverName + '_t';
    }

    // Create a modified file object for thumbnail. Clear url/thumb fields so
    // getUrl() reconstructs from the _t-suffixed serverName instead of
    // returning the full-size backend url (which would defeat the thumbnail).
    const thumbFile: ServerFile = {
      ...file,
      url: undefined,
      thumbnailUrl: undefined,
      thumbUrl: undefined,
      serverName: thumbServerName
    };

    return this.getUrl(thumbFile);
  }

  /**
   * Build path with directory structure from serverName
   * Pattern: xx/xx/xx/serverName (first 6 chars as 3 directory levels)
   */
  buildPathWithStructure(serverName: string, basePath: string = ''): string {
    if (!serverName || serverName.length < 6) {
      return basePath + serverName;
    }

    const dir1 = serverName.substring(0, 2);
    const dir2 = serverName.substring(2, 4);
    const dir3 = serverName.substring(4, 6);
    
    return `${basePath}${dir1}/${dir2}/${dir3}/${serverName}`;
  }

  /**
   * Get CMS product/category image URL
   * These images are stored on S3 with structure: cmsproimages/xx/xx/xx/serverName
   * 
   * @param image - ImageInfo object from API (has serverName, serverId, pathStructureId)
   * @returns Full URL to the image
   * 
   * Example:
   * serverName: 2dbcf0469d8c5ea5204db8ad3d5959cd.png
   * Result: https://express-cloud-storage-1.s3.eu-west-1.amazonaws.com/cmsproimages/2d/bc/f0/2dbcf0469d8c5ea5204db8ad3d5959cd.png
   */
  getCmsImageUrl(image: any): string {
    if (!image) return '';
    
    // If image already has a full URL, return it
    if (image.url && image.url.startsWith('http')) {
      return image.url;
    }
    
    // Get serverName from image object
    const serverName = image.serverName;
    if (!serverName) return '';
    
    // Build path with directory structure
    if (serverName.length < 6) {
      return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/${this.cmsImagesPath}/${serverName}`;
    }
    
    const dir1 = serverName.substring(0, 2);
    const dir2 = serverName.substring(2, 4);
    const dir3 = serverName.substring(4, 6);
    
    return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/${this.cmsImagesPath}/${dir1}/${dir2}/${dir3}/${serverName}`;
  }

  /**
   * Append a cache-bust query parameter to a URL so the browser HTTP cache
   * doesn't serve a stale response. Used by the preview pipeline (.order
   * file fetch, .dbproject metadata + file fetches) where the same URL can
   * point at different content after a save and the browser would otherwise
   * return the old version.
   *
   * Idempotent — if the URL already carries `_t=` or `_v=` we leave it
   * alone (safe to call from multiple layers without compounding params).
   *
   * Pass `version` when you have a content-version string (e.g. last-
   * modified timestamp from the draft record) — same content yields the
   * same URL, so the cache works again. Omit `version` for "always
   * fresh" semantics (`_t=${Date.now()}`).
   *
   * @param url Source URL.
   * @param version Optional content-version string. When omitted uses Date.now().
   * @returns URL with the cache-bust query param appended.
   */
  withCacheBust(url: string, version?: string | number): string {
    if (!url) return url;
    if (/[?&]_[tv]=/.test(url)) return url; // already busted
    const param = version !== undefined && version !== null && version !== ''
      ? `_v=${encodeURIComponent(String(version))}`
      : `_t=${Date.now()}`;
    return url.includes('?') ? `${url}&${param}` : `${url}?${param}`;
  }

  /**
   * Get user avatar URL
   * Avatars are stored on S3 with structure: express/avatars/xx/xx/serverName
   * 
   * @param image - ImageInfo object from API (has serverName, serverId, pathStructureId)
   * @returns Full URL to the avatar image
   * 
   * Example:
   * serverName: 451187e68dfaefea74ba32511c144a83.jpeg
   * pathStructureId: 13
   * Result: https://express-cloud-storage-1.s3.eu-west-1.amazonaws.com/express/avatars/45/11/451187e68dfaefea74ba32511c144a83.jpeg
   */
  getAvatarUrl(image: any): string {
    if (!image) return '';

    // If image already has a full URL, return it
    if (image.url && image.url.startsWith('http')) {
      return image.url;
    }

    // Get serverName from image object
    const serverName = image.serverName;
    if (!serverName) return '';

    // Build path with directory structure (avatars use 2 levels: xx/xx/)
    if (serverName.length < 4) {
      return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/${this.avatarsPath}/${serverName}`;
    }

    const dir1 = serverName.substring(0, 2);
    const dir2 = serverName.substring(2, 4);

    return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/${this.avatarsPath}/${dir1}/${dir2}/${serverName}`;
  }

  /**
   * Get theme preview image URL
   * Theme previews are stored on S3 with structure: express/theme_previews/approved/xx/xx/xx/hash.png
   *
   * @param hash - The preview hash (e.g., "67c0afb1005abb095cfe2bc1aa989dfe")
   * @returns Full URL to the theme preview image
   *
   * Example:
   * hash: 67c0afb1005abb095cfe2bc1aa989dfe
   * Result: https://express-cloud-storage-1.s3.eu-west-1.amazonaws.com/express/theme_previews/approved/67/c0/af/67c0afb1005abb095cfe2bc1aa989dfe.png
   */
  getThemePreviewUrl(hash: string): string {
    // Ensure hash is a valid string
    if (!hash || typeof hash !== 'string' || hash.length < 6) return '';

    const dir1 = hash.substring(0, 2);
    const dir2 = hash.substring(2, 4);
    const dir3 = hash.substring(4, 6);

    return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/express/theme_previews/approved/${dir1}/${dir2}/${dir3}/${hash}.png`;
  }

  /**
   * Get theme project URL (.theme file) from drafts folder
   * Theme projects are stored on S3 with structure: express/themes/drafts/xx/xx/xx/serverName.theme
   *
   * @param serverName - The theme serverName (hash)
   * @returns Full URL to the .theme file, or null if invalid
   */
  getThemeProjectUrl(serverName: string): string | null {
    return this.getThemeDraftUrl(serverName, 'theme');
  }

  /**
   * Get theme project JSON URL (.json file) from drafts folder
   * JSON versions of themes are stored alongside .theme files
   * Pattern: express/themes/drafts/xx/xx/xx/serverName.json
   *
   * @param serverName - The theme serverName (hash)
   * @returns Full URL to the .json file, or null if invalid
   */
  getThemeProjectJsonUrl(serverName: string): string | null {
    return this.getThemeDraftUrl(serverName, 'json');
  }

  /**
   * Get theme URL from drafts folder (for Designer/editing)
   * Pattern: express/themes/drafts/xx/xx/xx/serverName.{extension}
   *
   * @param serverName - The theme serverName (hash)
   * @param extension - File extension ('theme' or 'json')
   * @returns Full URL to the file, or null if invalid
   */
  getThemeDraftUrl(serverName: string, extension: 'theme' | 'json' = 'json'): string | null {
    if (!serverName || typeof serverName !== 'string' || serverName.length < 6) {
      return null;
    }

    const dir1 = serverName.substring(0, 2);
    const dir2 = serverName.substring(2, 4);
    const dir3 = serverName.substring(4, 6);

    return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/express/themes/drafts/${dir1}/${dir2}/${dir3}/${serverName}.${extension}`;
  }

  /**
   * Get theme URL from approved folder (for end-user/order mode)
   * Pattern: express/themes/approved/xx/xx/xx/serverName.{extension}
   *
   * @param serverName - The theme serverName (hash)
   * @param extension - File extension ('theme' or 'json')
   * @returns Full URL to the file, or null if invalid
   */
  getThemeApprovedUrl(serverName: string, extension: 'theme' | 'json' = 'json'): string | null {
    if (!serverName || typeof serverName !== 'string' || serverName.length < 6) {
      return null;
    }

    const dir1 = serverName.substring(0, 2);
    const dir2 = serverName.substring(2, 4);
    const dir3 = serverName.substring(4, 6);

    return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/express/themes/approved/${dir1}/${dir2}/${dir3}/${serverName}.${extension}`;
  }

  /**
   * Get gallery photo URL
   * Gallery photos (from GalleryService) are stored based on pathStructureId
   *
   * Path structures by ID:
   * - 16: express/themes_photos/xx/serverName (theme/gallery photos)
   * - 20: express/themes/drafts/xx/xx/xx/serverName (theme project files)
   *
   * @param serverName - The photo serverName (hash with extension)
   * @param pathStructureId - The path structure ID (default 16 for gallery photos)
   * @param size - Optional size suffix: '', 't' (thumbnail), 's' (small), 'm' (medium), 'b' (big)
   * @returns Full URL to the gallery photo
   *
   * Example:
   * serverName: 3b9455d6eda1158104c82146b54ff442.png
   * pathStructureId: 16
   * size: 't'
   * Result: https://express-cloud-storage-1.s3.eu-west-1.amazonaws.com/express/themes_photos/3b/3b9455d6eda1158104c82146b54ff442_t.png
   *
   * For pathStructureId 20 (DemoPhotoInfo):
   * serverName: bf462b010731e0b8c851ff7a84e1f0ab.jpg
   * Result: https://express-cloud-storage-1.s3.eu-west-1.amazonaws.com/express/demo_photos/bf/46/2b/bf462b010731e0b8c851ff7a84e1f0ab.jpg
   */
  getGalleryPhotoUrl(serverName: string, pathStructureId: number = 20, size: '' | 't' | 's' | 'm' | 'b' = ''): string {
    if (!serverName || typeof serverName !== 'string' || serverName.length < 6) {
      console.warn('[PathService] getGalleryPhotoUrl: Invalid serverName:', serverName);
      return '';
    }

    // Apply size suffix to serverName
    let finalServerName = serverName;
    if (size) {
      const lastDot = serverName.lastIndexOf('.');
      if (lastDot > 0) {
        finalServerName = serverName.substring(0, lastDot) + '_' + size + serverName.substring(lastDot);
      } else {
        finalServerName = serverName + '_' + size;
      }
    }

    // Build path based on pathStructureId
    const dir1 = serverName.substring(0, 2);

    // Path structure 16: express/themes_photos/xx/serverName (2-level)
    if (pathStructureId === 16) {
      return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/express/themes_photos/${dir1}/${finalServerName}`;
    }

    const dir2 = serverName.substring(2, 4);
    const dir3 = serverName.substring(4, 6);

    // Path structure 21: Hetzner Object Storage for low-res gallery photos
    if (pathStructureId === 21) {
      return `${this.hetznerBaseUrl}/lr-gallery-photos/${dir1}/${dir2}/${dir3}/${finalServerName}`;
    }

    // Path structure 20: express/galleries/photos/xx/xx/xx/serverName (3-level for gallery photos)
    return `${this.cmsImagesProtocol}://${this.cmsImagesServer}/express/galleries/photos/${dir1}/${dir2}/${dir3}/${finalServerName}`;
  }

  /**
   * Check if we're running on localhost (development mode)
   * When on localhost, use relative URLs so the Angular proxy can intercept requests
   */
  private isLocalhost(): boolean {
    return typeof window !== 'undefined' &&
           (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  }

  /**
   * Get the upload URL for theme preview images
   * POST multipart/form-data to UploadThemePreview.php
   *
   * @returns Full URL to the upload endpoint (relative on localhost for proxy)
   */
  getUploadThemePreviewUrl(): string {
    // Use relative path on localhost so Angular proxy can intercept
    if (this.isLocalhost()) {
      return '/UploadThemePreview.php';
    }
    const config = this.globals.config();
    const protocol = config.ssl ? 'https' : 'http';
    const server = config.defaultServer || 'www.dreambooks.pt';
    return `${protocol}://${server}/UploadThemePreview.php`;
  }

  /**
   * Get the upload URL for theme project bytes
   * POST JSON to UploadThemeBytes.php
   *
   * @returns Full URL to the upload endpoint (relative on localhost for proxy)
   */
  getUploadThemeBytesUrl(): string {
    // Use relative path on localhost so Angular proxy can intercept
    if (this.isLocalhost()) {
      return '/UploadThemeBytes.php';
    }
    const config = this.globals.config();
    const protocol = config.ssl ? 'https' : 'http';
    const server = config.defaultServer || 'www.dreambooks.pt';
    return `${protocol}://${server}/UploadThemeBytes.php`;
  }
}
